import React, { useState } from 'react';
import { Modal, Table, Button, Input, Form, ColorPicker, notification } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { BoardService } from "./boardService";

const LabelManagement = ({ open, onClose, labels, boardId, onLabelsChange }) => {
    const [form] = Form.useForm();
    const [editingLabel, setEditingLabel] = useState(null);
    const [loading, setLoading] = useState(false);

    const startEditing = (label) => {
        setEditingLabel({
            id: label.id,
            name: label.name,
            description: label.description,
            color: label.color
        });
    };

    const cancelEditing = () => {
        setEditingLabel(null);
    };

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const colorHex = values.color;
            const newLabel = await BoardService.createLabel(boardId, {
                name: values.name,
                description: values.description,
                color: (typeof colorHex === 'string' ? colorHex : colorHex?.toHexString())
            });
            onLabelsChange([...labels, newLabel]);
            form.resetFields();
            notification.success({
                message: 'Label Added',
                description: `Label "${values.name}" has been added successfully.`
            });
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to add label. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const saveEdit = async () => {
        if (!editingLabel) return;

        try {
            setLoading(true);
            const updatedLabel = await BoardService.updateLabel(boardId, editingLabel.id, {
                name: editingLabel.name,
                description: editingLabel.description,
                color: editingLabel.color
            });
            onLabelsChange(labels.map(label =>
                label.id === editingLabel.id ? updatedLabel : label
            ));
            setEditingLabel(null);
            notification.success({
                message: 'Label Updated',
                description: 'Label has been updated successfully.'
            });
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to update label. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (label) => {
        try {
            setLoading(true);
            await BoardService.deleteLabel(boardId, label.id);
            onLabelsChange(labels.filter(l => l.id !== label.id));
            notification.success({
                message: 'Label Deleted',
                description: `Label "${label.name}" has been deleted successfully.`
            });
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete label. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = (label) => {
        Modal.confirm({
            title: 'Delete Label',
            content: (
                <div>
                    <p>{`Are you sure you want to delete "${label.name}"?`}</p>
                    <div className="flex items-center gap-2 mt-2 p-2 bg-gray-50 rounded-lg">
                        <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: label.color }}
                        />
                        <span className="text-gray-600">
                            This will remove the label from all associated tasks
                        </span>
                    </div>
                </div>
            ),
            okText: 'Delete',
            okButtonProps: {
                danger: true,
                className: 'bg-red-500'
            },
            onOk: () => handleDelete(label),
        });
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => {
                const isEditing = editingLabel?.id === record.id;
                return isEditing ? (
                    <Input
                        value={editingLabel.name}
                        onChange={(e) => setEditingLabel({
                            ...editingLabel,
                            name: e.target.value
                        })}
                        className="w-full"
                        autoFocus
                    />
                ) : (
                    <div className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: record.color }}
                        />
                        <span className="cursor-pointer hover:text-blue-500">
                            {text}
                        </span>
                    </div>
                );
            },
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text, record) => {
                const isEditing = editingLabel?.id === record.id;
                return isEditing ? (
                    <Input
                        value={editingLabel.description}
                        onChange={(e) => setEditingLabel({
                            ...editingLabel,
                            description: e.target.value
                        })}
                        className="w-full"
                    />
                ) : (
                    <span className="text-gray-600">{text}</span>
                );
            },
        },
        {
            title: 'Color',
            dataIndex: 'color',
            key: 'color',
            width: 200,
            render: (color, record) => {
                const isEditing = editingLabel?.id === record.id;
                return isEditing ? (
                    <ColorPicker
                        value={editingLabel.color}
                        onChange={(_color, hex) => setEditingLabel({
                            ...editingLabel,
                            color: _color.toHexString()
                        })}
                        size="middle"
                        presets={[
                            {
                                label: 'Recommended',
                                colors: [
                                    '#1677FF', '#2ECC71', '#E74C3C',
                                    '#F1C40F', '#9B59B6', '#34495E',
                                    '#16A085', '#E67E22', '#95A5A6'
                                ],
                            },
                        ]}
                    />
                ) : (
                    <div className="flex items-center gap-2">
                        <div
                            className="w-8 h-8 rounded-lg shadow-sm"
                            style={{ backgroundColor: color }}
                        />
                        <span className="text-gray-500 text-sm">{color}</span>
                    </div>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => {
                const isEditing = editingLabel?.id === record.id;
                return isEditing ? (
                    <div className="flex items-center gap-2">
                        <Button
                            type="text"
                            icon={<CheckOutlined />}
                            onClick={saveEdit}
                            className="hover:bg-green-50 hover:text-green-500"
                        />
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={cancelEditing}
                            className="hover:bg-red-50 hover:text-red-500"
                        />
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => startEditing(record)}
                            className="hover:bg-gray-100 hover:text-blue-500"
                        />
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => confirmDelete(record)}
                            className="hover:bg-red-50"
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <Modal
            title={
                <div className="flex items-center gap-3">
                    <span className="text-lg">Manage Labels</span>
                    <span className="text-sm text-gray-400">
                        ({labels.length} total)
                    </span>
                </div>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={800}
            className="rounded-xl"
        >
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <Form
                    form={form}
                    layout="horizontal"
                    onFinish={handleAdd}
                    className="flex items-center gap-3"
                >
                    <Form.Item
                        name="name"
                        rules={[{ required: true, message: 'Label name is required' }]}
                        className="flex-1 mb-0"
                    >
                        <Input
                            placeholder="Enter label name"
                            size="large"
                            className="rounded-lg"
                        />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        rules={[{ required: true, message: 'Description is required' }]}
                        className="flex-1 mb-0"
                    >
                        <Input
                            placeholder="Enter description"
                            size="large"
                            className="rounded-lg"
                        />
                    </Form.Item>
                    <Form.Item
                        name="color"
                        rules={[{ required: true, message: 'Color is required' }]}
                        className="mb-0"
                    >
                        <ColorPicker
                            size="large"
                            presets={[
                                {
                                    label: 'Recommended',
                                    colors: [
                                        '#1677FF', '#2ECC71', '#E74C3C',
                                        '#F1C40F', '#9B59B6', '#34495E',
                                        '#16A085', '#E67E22', '#95A5A6'
                                    ],
                                },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item className="mb-0">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            loading={loading}
                            htmlType="submit"
                            size="large"
                            className="text-sm font-semibold leading-tight px-5 py-2.5 relative items-center rounded-full justify-center border border-gray-200 bg-[#3d8bfd]"
                        >
                            Add Label
                        </Button>
                    </Form.Item>
                </Form>
            </div>

            <Table
                columns={columns}
                dataSource={labels}
                rowKey="id"
                pagination={false}
                loading={loading}
                className="rounded-lg border border-gray-100"
                rowClassName="hover:bg-gray-50"
            />
        </Modal>
    );
};

export default LabelManagement;
